import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isRealtimeEventEnvelope } from '@beatq/shared';
import { useBranchIdParam } from '../branch-settings/branchSettingsState.js';
import {
  bindMerchantSocketLive,
  connectMerchantOpsSocket,
  merchantOpsPollMs,
  parseRealtimeTicketEnvelope,
  type MerchantOpsSocket,
} from '../realtime/merchantRealtimeConnect.js';

export { merchantOpsPollMs, parseRealtimeTicketEnvelope };

/** Query prefixes invalidated on the shared branch orders room (G-M09-RT). */
export const MERCHANT_ORDER_REALTIME_QUERY_KEYS = [
  ['merchant', 'orders', 'board'],
  ['merchant', 'orders'],
  ['merchant', 'order'],
  ['merchant', 'kitchen'],
  ['merchant', 'pick-list'],
  ['merchant', 'notifications'],
] as const;

/**
 * Mint a merchant realtime ticket, subscribe to `branch:{id}:orders`, and invalidate
 * board/kitchen/pick-list queries on order-ish events. Poll remains the fallback.
 * `live` is true only while the Socket.IO connection is actually connected.
 */
export function useMerchantOrderRealtime(enabled = true): { live: boolean } {
  const branchId = useBranchIdParam();
  const qc = useQueryClient();
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!enabled || !branchId) {
      setLive(false);
      return;
    }

    let cancelled = false;
    let socket: MerchantOpsSocket | null = null;
    let unbindLive: (() => void) | undefined;

    const invalidateOps = () => {
      for (const queryKey of MERCHANT_ORDER_REALTIME_QUERY_KEYS) {
        void qcRef.current.invalidateQueries({ queryKey: [...queryKey] });
      }
    };

    const onAnyEvent = (raw: unknown) => {
      if (isRealtimeEventEnvelope(raw) || raw != null) invalidateOps();
    };

    void (async () => {
      try {
        socket = await connectMerchantOpsSocket(branchId);
        if (cancelled) return;
        if (!socket) {
          setLive(false);
          return;
        }
        unbindLive = bindMerchantSocketLive(socket, (next) => {
          if (!cancelled) setLive(next);
        });
        socket.onAny(onAnyEvent);
      } catch (err) {
        if (!cancelled) setLive(false);
        console.warn('[merchant-realtime] connect skipped', err);
      }
    })();

    return () => {
      cancelled = true;
      setLive(false);
      unbindLive?.();
      if (socket) socket.offAny(onAnyEvent);
    };
  }, [enabled, branchId]);

  return { live };
}
